import React, { useEffect, useState, Suspense } from "react";
import {
  Route,
  Routes,
  Link,
  Navigate,
} from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client'; // Use @apollo/client
import { useSelector, useDispatch } from 'react-redux';
import { setcurrent } from './redux/currentpublisher/currentpublisherSlice.js';
import { setpublisherGlobalData } from './redux/currentpublisher/publisherGlobalDataSlice.js';
import { setcontribcodes } from './redux/onixcontributorcodes/contribcodesSlice.js';
import { setonixGlobalData } from './redux/onix/onixGlobalDataSlice.js';
import { setall } from './redux/publishers/publishersSlice.js';
import { setuser } from './redux/user/userSlice.js';
import { setprofile } from './redux/user/profileSlice.js';
import UserMenu from "./components/layout/UserMenu.js";
import SideBar from "./components/layout/SideBar.js";
import MobileNav from "./components/layout/MobileNav.js";
import logo from './assets/images/logo-orange.png';
import QUERY_GLOBAL_DATA from "./graphql/queries/QueryGlobalData.js";
import './assets/styles/index.css';
import inboxmsgs from './inboxmsgs.json';
import { getCookie, makeDateString } from "./util.js";
import JSON5 from 'json5';
import {
  Pane,
  Heading,
  Paragraph,
  SideSheet,
  Button,
  InboxIcon,
  Pill,
  ThemeProvider,
} from 'evergreen-ui';
import InboxItem from "./components/layout/InboxItem.js";
import UPDATE_PUBLISHER_MUTATION from "./graphql/mutations/PublisherData/UpdatePublisher.js";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase_setup/firebase.js";
import * as Sentry from "@sentry/react";
import customTheme from './theme'
import packageJson from '../package.json'

// Auto-reload on chunk load failure (stale bundles after deploy)
function lazyWithRetry(importFn) {
  return React.lazy(() =>
    importFn().catch(() => {
      // Only reload once to avoid infinite loops
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // Prevent render during reload
      }
      sessionStorage.removeItem('chunk_reload');
      return importFn(); // Re-throw if already reloaded once
    })
  );
}

// Clear the reload flag on successful page load
sessionStorage.removeItem('chunk_reload');

// Route components — lazy-loaded for code splitting
const Dash = lazyWithRetry(() => import("./views/Dashboard/Dash.js"));
const UserSettings = lazyWithRetry(() => import("./views/Settings/UserSettings.js"));
const PublisherSettings = lazyWithRetry(() => import("./views/Settings/PublisherSettings.js"));
const Creators = lazyWithRetry(() => import("./views/Creators/Creators.js"));
const Creator = lazyWithRetry(() => import("./views/Creators/Creator.js"));
const Products = lazyWithRetry(() => import("./views/Products/Products.js"));
const Help = lazyWithRetry(() => import("./views/Help/Help.js"));
const Product = lazyWithRetry(() => import("./views/Products/Product.js"));
const Customers = lazyWithRetry(() => import("./views/Customers/Customers.js"));
const Customer = lazyWithRetry(() => import("./views/Customers/Customer.js"));
const Orders = lazyWithRetry(() => import("./views/Orders/Orders.js"));
const Order = lazyWithRetry(() => import("./views/Orders/Order.js"));
const Expenses = lazyWithRetry(() => import("./views/Expenses/Expenses.js"));
const Expense = lazyWithRetry(() => import("./views/Expenses/Expense.js"));
const Royalties = lazyWithRetry(() => import("./views/Royalties/Royalties.js"));
const Royalty = lazyWithRetry(() => import("./views/Royalties/Royalty.js"));
const RoyaltyPayments = lazyWithRetry(() => import("./views/RoyaltyPayments/RoyaltyPayments.js"));
const Statements = lazyWithRetry(() => import("./views/Statements/Statements.js"));
const Reporting = lazyWithRetry(() => import("./views/Reporting/Reporting.js"));
const ResetPassword = lazyWithRetry(() => import("./views/Login/ResetPassword.js"));
const ResetNewPassword = lazyWithRetry(() => import("./views/Login/ResetNewPassword.js"));
const NotFound = lazyWithRetry(() => import("./views/NotFound.js"));
const Settings = lazyWithRetry(() => import("./views/Settings/Settings"));
const Impersonate = lazyWithRetry(() => import("./impersonate/impersonate.js"));
const SuperAdminDashboard = lazyWithRetry(() => import("./components/adminsuper/Dashboard.js"));
const RoyaltyCalculator = lazyWithRetry(() => import("./components/adminsuper/tools/RoyaltyCalculator.js"));

function App(props) {
  const userid = useSelector((state) => state.user.value);
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const [showInbox, setShowInbox] = useState(false);
  const [fbAuth, setFbAuth] = useState(publisherGlobalData.firebaseAuth);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [updatePublisher] = useMutation(UPDATE_PUBLISHER_MUTATION);
  const dispatch = useDispatch();

  // Poll for new app version every 2 minutes
  useEffect(() => {
    const checkVersion = () => {
      fetch('/meta.json?t=' + Date.now())
        .then(res => res.json())
        .then(meta => {
          if (meta.buildDate && packageJson.buildDate && meta.buildDate > packageJson.buildDate) {
            setNewVersionAvailable(true);
          }
        })
        .catch(() => {});
    };
    const interval = setInterval(checkVersion, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const currYear = new Date().getFullYear();
  const newNotifCount = inboxmsgs.filter(item => {
    let inboxDate = new Date(publisherGlobalData.inboxDate);
    return new Date(Date.parse(item.date)) > inboxDate;
  }).length;

  const handleSetInboxDate = () => {
    const tomorrow = new Date().setDate(new Date().getDate() + 1);
    const tomorrowDate = new Date(tomorrow);
    const todayDate = makeDateString(tomorrowDate);
    dispatch(setpublisherGlobalData({ inboxDate: todayDate }));
    updatePublisher({
      variables: {
        id: parseInt(currentpublisher.id),
        changed: JSON.stringify([{ key: "inboxDate", val: todayDate }]),
      }
    });
  };

  useEffect(() => {
    dispatch(setuser(props.user));
    if (props.publisher) {
      dispatch(setcurrent({ id: props.publisher, name: props.publisherName }));
    }
  }, [props.publisherName, props.publisher, props.user, dispatch]);

  const intId = parseInt(userid);
  const noData = !currentpublisher.id || isNaN(intId);

  const createContribCodeArray = codes => {
    return codes.map(code => ({
      label: `${code.value} - ${code.description}`,
      value: code.id,
    }));
  };

  useEffect(() => {
    if (!fbAuth && currentpublisher.id) {
      const csrftoken = getCookie('csrftoken');
      fetch('/fetch_firebase_credentials/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        }
      })
      .then(res => res.json())
      .then(data => {
        let obj = JSON5.parse(data.message);
        signInWithEmailAndPassword(auth, obj.user, obj.pass)
          .then(() => {
            return updatePublisher({
              variables: {
                id: parseInt(currentpublisher.id),
                changed: JSON.stringify([{ key: "firebaseAuth", val: true }]),
              }
            });
          })
          .then(() => {
            setFbAuth(true);
          })
          .catch(error => {
            console.error("Firebase Auth Error:", error);
            Sentry.captureException(error); // Capture any Firebase errors
          });
      });
    }
  }, [fbAuth, currentpublisher.id, updatePublisher]);

  useEffect(() => {
    if (fbAuth) {
      dispatch(setpublisherGlobalData({ firebaseAuth: true }));
    }
  }, [fbAuth, dispatch]);

  const { loading, error, data } = useQuery(QUERY_GLOBAL_DATA, {
    variables: { user: intId, publisher: parseInt(currentpublisher.id) },
    skip: noData
  });

  useEffect(() => {
    if (data && data.userpublishers) {
      let codeArray = createContribCodeArray(data.onixcontributorcodes);
      dispatch(setcontribcodes(codeArray));
      let onixObj = {
        onixcontributorcodes: data.onixcontributorcodes,
        onixproductforms: data.onixproductforms,
        onixproductformdetails: data.onixproductformdetails,
        onixproductavailabilitys: data.onixproductavailabilitys,
        onixaudiencetypes: data.onixaudiencetypes,
      };
      dispatch(setonixGlobalData(onixObj));
      let publisherObj = {
        created: data.publisher.created,
        inboxDate: data.publisher.inboxDate,
        numproducts: data.products.length,
        publisherimprints: data.publisherimprints,
        publishertags: data.publishertags,
        producttypes: data.producttypes,
        productseriess: data.productseriess,
        publishercreators: data.publishercreators,
        customerclasses: data.customerclasses,
        paymentterms: data.paymentterms,
        shippingtypes: data.shippingtypes,
        salespersons: data.publishersalespersons,
        paymenttypes: data.paymenttypes,
        orderstatuss: data.orderstatuss,
        expensecategorys: data.expensecategorys,
        royaltycategorys: data.royaltycategorys,
        royaltyterms: data.royaltyterms,
        currencys: data.currencys,
        currency: data.publisher.currency,
        accountingMethod: data.publisher.accountingMethod,
        billingId: data.publisher.billingId,
        billingDate: data.publisher.billingDate,
        billingActive: data.publisher.billingActive,
        plan: data.publisher.plan,
        maxProducts: data.publisher.maxProducts,
        firebaseAuth: data.publisher.firebaseAuth,
        enableSalesByRegion: data.publisher.enableSalesByRegion,
        enable_sales_by_region: data.publisher.enableSalesByRegion,
        regions: data.publisher.regions || [],
      };

      // Check if royaltyterms is empty or undefined and provide default values if needed
      if (!publisherObj.royaltyterms || !Array.isArray(publisherObj.royaltyterms) || publisherObj.royaltyterms.length === 0) {
        publisherObj.royaltyterms = [
          { id: 1, value: "of list price" },
          { id: 2, value: "of net receipts" },
          { id: 3, value: "of net profits" },
          { id: 4, value: "of true profits" }
        ];
      }

      dispatch(setpublisherGlobalData(publisherObj));
      if (data.userpublishers && data.userpublishers.length > 0) {
        const currPubId = data.userpublishers[0].publisher.id;
        const currPubName = data.userpublishers[0].publisher.name;
        if (!currentpublisher || !currentpublisher.id) {
          dispatch(setcurrent({ id: currPubId }));
        }
        if (!currentpublisher || !currentpublisher.name) {
          dispatch(setcurrent({ name: currPubName }));
        }
      }
      dispatch(setall(data.userpublishers));
    }
  }, [data, currentpublisher, dispatch]);

  useEffect(() => {
    // This is where you fetch user data after login
    const fetchUserData = async () => {
      try {
        if (data && data.userpublishers && data.userpublishers.length > 0) {
          const userPublisher = data.userpublishers[0];
          dispatch(setprofile({
            ...userPublisher.publisher,
            // Include the publisher user ID
            publisherUserId: userPublisher.id,
            // And other user fields that might be needed
            firstName: userPublisher.firstName,
            lastName: userPublisher.lastName,
            email: userPublisher.user?.email,
            photoUrl: userPublisher.photoUrl,
          }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (data && data.userpublishers) {
      fetchUserData();
    }
  }, [data, dispatch]);

  if (loading) return null;
  if (error) {
    Sentry.captureException(error);
    return `Error! ${error}`;
  }
  if (!data || !data.userpublishers) {
    return null;
  }

  return (
    <ThemeProvider value={customTheme}>
      <Pane>
        {newVersionAvailable && (
          <Pane
            display="flex" alignItems="center" justifyContent="center"
            padding={10} backgroundColor="var(--darkgreen)" zIndex={9999}
            position="fixed" top={0} left={0} right={0}
          >
            <Paragraph color="white" marginRight={12} fontSize={14}>
              A new version of WorkingLit is available.
            </Paragraph>
            <Button appearance="primary" size="small" onClick={() => window.location.reload()}>
              Refresh now
            </Button>
          </Pane>
        )}
        <Pane id="outer-container" backgroundColor={"var(--cream)"} marginTop={newVersionAvailable ? 48 : 0}>
          <MobileNav />
          <Pane id="left-nav" elevation={1}>
            <Pane id="left-nav-inner">
              <Pane display="flex" flexDirection="column">
                <Heading id="logo-container">
                  <Link to='/'>
                    <img className="logo" src={logo} alt="working lit logo" />
                  </Link>
                </Heading>
                {props.baseUrl !== "https://app.workinglit.com" && props.baseUrl !== "https://www.workinglit.com" && props.baseUrl !== "https://workinglit.com" && process.env.NODE_ENV !== 'development' &&
                  <Paragraph size={400} fontWeight="bold" color="#e85cc9">STAGING SERVER!</Paragraph>}
                <UserMenu pubUser={data.userpublishers[0]} />
                <SideSheet isShown={showInbox} onCloseComplete={() => setShowInbox(false)} className="background" width="25vw">
                  <Pane display="flex" justifyContent="space-between" alignItems="center" margin={16}>
                    <Paragraph size={500} fontWeight="bolder" color="var(--darkgreen)">INBOX</Paragraph>
                    <Button size="small" disabled={newNotifCount === 0} onClick={handleSetInboxDate}>Mark all as read</Button>
                  </Pane>
                  {inboxmsgs.map(item => {
                    const isUnread = !publisherGlobalData.inboxDate || new Date(Date.parse(item.date)) > new Date(publisherGlobalData.inboxDate);
                    return <InboxItem key={`${item.date}_${item.title.replace(/\s/g, "")}`} item={item} unread={isUnread} />;
                  })}
                </SideSheet>
                <Button marginTop={4} iconBefore={InboxIcon} className="inbox-button" onClick={() => setShowInbox(!showInbox)}>
                  <Pane display='flex' justifyContent="space-between" alignItems="center">
                    <Paragraph className="inbox-button-text">Inbox</Paragraph>
                    {newNotifCount > 0 && <Pill color={"orange"} marginLeft={16}>{newNotifCount}</Pill>}
                  </Pane>
                </Button>
                <Routes>
                  <Route path='/:page' element={<SideBar />} />
                  <Route path='/' element={<SideBar />} />
                  <Route path={`/creators/:creatorId`} element={<SideBar />} />
                  <Route path={`/products/:productId`} element={<SideBar />} />
                  <Route path={`/customers/:customerId`} element={<SideBar />} />
                  <Route path={`/orders/:orderId`} element={<SideBar />} />
                  <Route path={`/expenses/:expenseId`} element={<SideBar />} />
                  <Route path={`/royalties/:royaltyId`} element={<SideBar />} />
                  <Route path="/statements" element={<SideBar />} />
                  <Route path={`/reporting/profit-loss`} element={<SideBar />} />
                  <Route path={`/reporting/top-products`} element={<SideBar />} />
                  <Route path={`/super`} element={<SideBar />} />
                </Routes>
              </Pane>
              <Pane>
                {process.env.NODE_ENV === 'development' &&
                  <Paragraph size={300} color="var(--lightorange)" marginBottom={8}>You are running this app in {process.env.NODE_ENV} mode.</Paragraph>}
                <Paragraph color="white" fontSize={10}>
                  <a className="link-dark" href="https://docs.google.com/document/d/1_kjfaoJzEogR4GNToaibl4377smPQVXHaO1OAw4UJbI/edit?usp=sharing">Terms of Service</a>
                </Paragraph>
                <Paragraph size={300} fontSize="10px" color='white'>Copyright &copy; {currYear}</Paragraph>
                <Paragraph size={300} fontSize="10px" color='white'>Microcosm Publishing</Paragraph>
              </Pane>
            </Pane>
          </Pane>

          <Pane id="main-container" elevation={0}>
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={<Dash />} />
              <Route path="/user" element={<UserSettings />} />
              <Route path="/publisher" element={<PublisherSettings />} />
              <Route path="/creators" element={<Creators />} />
              <Route path="/creators/:creatorId" element={<Creator />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:productId" element={<Product />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:customerId" element={<Customer />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:orderId" element={<Order />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/expenses/:expenseId" element={<Expense />} />
              <Route path="/royalties" element={<Royalties />} />
              <Route path="/royalties/:royaltyId" element={<Royalty />} />
              <Route path="/royaltypayments" element={<RoyaltyPayments />} />
              <Route path="/statements" element={<Statements />} />
              <Route path="/reporting" element={<Reporting />} />
              <Route path="/reporting/profit-loss" element={<Reporting />} />
              <Route path="/reporting/top-products" element={<Reporting />} />
              <Route path="/reset_password" element={<ResetPassword />} />
              <Route path="/reset_password/new" element={<ResetNewPassword />} />
              <Route path="/impersonate" element={<Impersonate />} />
              <Route path="/help" element={<Help />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/super" element={<SuperAdminDashboard />} />
              <Route path="/super/royalty_calculator" element={<RoyaltyCalculator />} />
              <Route path="/super/tools/royalty-calculator" element={<RoyaltyCalculator />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Pane>
        </Pane>
      </Pane>
    </ThemeProvider>
  );
}

export default App;
